import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Box, Button, Card, Chip, IconButton, InputAdornment, Skeleton, Table, TableBody, TableCell, TableHead, TablePagination, TableRow,
  TextField, Tooltip, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ClearIcon from '@mui/icons-material/Clear'
import SearchIcon from '@mui/icons-material/Search'
import { useNavigate, useSearchParams } from 'react-router-dom'
import EmptyState from '../merchants/shared/EmptyState'
import { ReadinessChip } from '../merchants/shared/StateChip'
import { C, RADIUS, SX, TYPE } from '../merchants/shared/tokens'
import { errorText, fetchMerchants, loadAgreementsInto } from './ptlSetupApi'
import { ONBOARDING_STEPS, ROUTES, num } from './constants'
import { agreementStatus, isFullySetUp, nextStepKey, readiness, statusOf, stepStates } from './components/MerchantStatus'
import { Banner, SR_ONLY, merchantLabel } from './components/SetupUi'
import OnboardMerchantDialog from './components/OnboardMerchantDialog'
import AgreementDialog from './components/AgreementDialog'
import PtlPage from './shell/PtlPage'

/** KPI cards double as the list filter (?view=). Order = the order of the cards. */
const VIEWS = [
  { key: 'all', label: 'PTL merchants', test: () => true },
  { key: 'ready', label: 'Ready to book', test: (m) => readiness(m).ready },
  { key: 'complete', label: 'Fully set up', test: (m) => isFullySetUp(m) },
  { key: 'no-kyc', label: 'KYC not approved', test: (m) => !readiness(m).kyc.ok },
  { key: 'no-pickup', label: 'No pickup location', test: (m) => !readiness(m).pickup.ok },
  { key: 'no-rate-card', label: 'Missing rate card', test: (m) => !statusOf(m).pricing },
  { key: 'no-allocation', label: 'Missing allocation', test: (m) => !statusOf(m).allocation },
  { key: 'custom-tat', label: 'On custom TAT', test: (m) => statusOf(m).customTat },
  { key: 'own-map', label: 'Own zone map', test: (m) => statusOf(m).ownMap },
  { key: 'agreement-expiry', label: 'Agreement expiring', test: (m) => agreementStatus(m.agreement).expiry },
  { key: 'agreement-open', label: 'Agreement not approved', test: (m) => !!m.agreement && m.agreement.approvalStatus !== 'APPROVED' },
]

const PAGE_SIZES = [25, 50, 100]

/** The action column stays in view when the table scrolls sideways on a narrow screen. */
const STICKY_END = { position: 'sticky', right: 0, zIndex: 1, boxShadow: `-1px 0 0 ${C.line}` }


const pct = (n, total) => (total ? `${Math.round((n / total) * 100)}% of PTL merchants` : '')

/**
 * PTL FREIGHT → PTL Merchant Setup → Onboarding → Onboarding Health (plan screen 1). One screen for the health of PTL
 * onboarding: KPI cards, then every live PTL merchant with its Step 1 Pricing → Step 2 TAT → Step 3 Allocation progress
 * (D2C Onboarding Progress pattern). A pending step opens that step for the merchant. ?view= and ?q= keep the filter in
 * the link.
 */
export default function PtlOnboardingHealth() {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const [merchants, setMerchants] = useState(null)
  const [error, setError] = useState('')
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(PAGE_SIZES[0])
  const [pickerOpen, setPickerOpen] = useState(false)
  const [agreementFor, setAgreementFor] = useState(null)

  const view = VIEWS.some((v) => v.key === params.get('view')) ? params.get('view') : 'all'
  const search = params.get('q') || ''

  const load = useCallback(() => {
    setError('')
    fetchMerchants().then((ms) => { setMerchants(ms); loadAgreementsInto(setMerchants) }).catch((e) => setError(errorText(e, 'Could not load PTL merchants')))
  }, [])
  useEffect(() => { load() }, [load])

  const setParam = (key, value) => {
    const next = new URLSearchParams(params)
    if (value && !(key === 'view' && value === 'all')) next.set(key, value)
    else next.delete(key)
    setParams(next, { replace: true })
    setPage(0)
  }

  const counts = useMemo(() => Object.fromEntries(VIEWS.map((v) => [v.key, (merchants || []).filter(v.test).length])), [merchants])
  const total = counts.all || 0
  // The warning window is agreementExpiringDays on PTL Billing settings; every merchant's agreement carries it.
  const expiringDays = (merchants || []).find((m) => m.agreement?.expiringDays != null)?.agreement.expiringDays

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase()
    const v = VIEWS.find((x) => x.key === view)
    return (merchants || [])
      .filter((m) => v.test(m) && (!q || merchantLabel(m).toLowerCase().includes(q) || m.id.toLowerCase().includes(q)))
      // Merchants with work left come first, then by name (the API sorts by name).
      .sort((a, b) => Number(isFullySetUp(a)) - Number(isFullySetUp(b)))
  }, [merchants, search, view])

  const kpi = (key, extra) => ({
    key, label: VIEWS.find((v) => v.key === key).label, value: num(counts[key]), active: view === key,
    onClick: () => setParam('view', view === key ? 'all' : key), ...extra,
  })
  const kpis = [
    kpi('all', { sub: 'Live, with the PTL merchant type on', active: view === 'all', onClick: () => setParam('view', 'all') }),
    kpi('ready', { sub: pct(counts.ready, total) || 'KYC, pickup location and rate card', tone: total && counts.ready === total ? 'ok' : undefined }),
    kpi('no-kyc', { sub: counts['no-kyc'] ? 'Orders are refused until approved' : 'Every merchant is approved', tone: counts['no-kyc'] ? 'danger' : 'ok' }),
    kpi('no-pickup', { sub: counts['no-pickup'] ? 'No warehouse to pick up from' : 'Every merchant has one', tone: counts['no-pickup'] ? 'danger' : 'ok' }),
    kpi('no-rate-card', { sub: counts['no-rate-card'] ? 'Orders get no price' : 'Every merchant has one', tone: counts['no-rate-card'] ? 'danger' : 'ok' }),
    kpi('no-allocation', { sub: counts['no-allocation'] ? 'Cheapest carded courier is used' : 'Every merchant has couriers', tone: counts['no-allocation'] ? 'warn' : 'ok' }),
    kpi('agreement-expiry', {
      sub: expiringDays == null ? 'Expired only: set the warning days on PTL Billing settings' : `Expired, or expiring within ${expiringDays} days`,
      tone: counts['agreement-expiry'] ? 'warn' : 'ok',
    }),
  ]
  // Views that describe a merchant rather than a gap: small filter chips next to the search, so the cards stay one row.
  const chips = [
    { key: 'complete', label: 'Steps 1-3 done' },
    { key: 'custom-tat', label: 'On custom TAT' },
    { key: 'own-map', label: 'Own zone map' },
    { key: 'agreement-open', label: 'Agreement not approved' },
  ]

  const openNext = (m) => {
    const next = nextStepKey(m, ONBOARDING_STEPS)
    navigate(next ? ROUTES.onboardingStep(m.id, next) : ROUTES.updateHub(m.id))
  }
  const doneIds = useMemo(() => new Set((merchants || []).filter(isFullySetUp).map((m) => m.id)), [merchants])
  const activeView = VIEWS.find((v) => v.key === view)

  return (
    <PtlPage title='Onboarding Health'
      description='Every live PTL merchant and how far its set-up has got: Step 1 Pricing, Step 2 TAT, Step 3 Allocation. Open a merchant to continue where it stopped.'
      actions={
        <Button variant='contained' startIcon={<AddIcon />} onClick={() => setPickerOpen(true)} sx={{ ...SX.cta, minHeight: 36, whiteSpace: 'nowrap' }}>
          Onboard a merchant
        </Button>
      }
      kpis={kpis} kpisLoading={!merchants && !error} kpiMinWidth={140}>
      {error && <Banner tone='danger' action={<Button size='small' onClick={load} sx={{ textTransform: 'none' }}>Retry</Button>}>{error}</Banner>}

      {merchants && total === 0 ? (
        <EmptyState title='No PTL merchants yet'
          body='Use Onboard a merchant to create one or to turn on PTL for an existing merchant.' />
      ) : (
        <Card elevation={0} sx={{ ...SX.card, overflow: 'hidden' }}>
          <Box sx={{ p: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, borderBottom: `1px solid ${C.line}`, bgcolor: C.surfaceSunken }}>
            <TextField size='small' placeholder='Search merchant or id' value={search} sx={{ width: { xs: '100%', sm: 320 }, bgcolor: C.surface }}
              onChange={(e) => setParam('q', e.target.value)}
              inputProps={{ 'aria-label': 'Search merchant' }}
              InputProps={{
                startAdornment: <InputAdornment position='start'><SearchIcon sx={{ fontSize: 18, color: C.inkGhost }} /></InputAdornment>,
                endAdornment: search ? (
                  <InputAdornment position='end'>
                    <IconButton size='small' aria-label='Clear search' edge='end' onClick={() => setParam('q', '')}><ClearIcon fontSize='small' /></IconButton>
                  </InputAdornment>
                ) : null,
              }} />
            <Box role='group' aria-label='More views' sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {chips.map((c) => {
                const on = view === c.key
                return (
                  <Chip key={c.key} clickable aria-pressed={on} onClick={() => setParam('view', on ? 'all' : c.key)}
                    label={`${c.label} · ${num(counts[c.key])}`} variant={on ? 'filled' : 'outlined'} size='small'
                    sx={{
                      height: 30, fontWeight: 600, fontSize: 12, borderRadius: RADIUS.pill, '& .MuiChip-label': { textTransform: 'none' },
                      ...(on ? { bgcolor: C.accentSoft, color: C.accent, border: `1px solid ${C.accent}` } : { color: C.inkMuted, borderColor: C.lineStrong, bgcolor: C.surface }),
                      '&.Mui-focusVisible': { outline: `2px solid ${C.accent}`, outlineOffset: 2 },
                    }} />
                )
              })}
            </Box>
            <Typography role='status' sx={{ ...TYPE.rowHelp, color: C.inkMuted, ml: { md: 'auto' } }}>
              {merchants ? `${num(rows.length)} of ${num(total)} merchants` : 'Loading merchants…'}
              {view !== 'all' && <> · <Box component='span' sx={{ fontWeight: 600, color: C.inkBody }}>{activeView.label}</Box></>}
            </Typography>
            {view !== 'all' && (
              <Button size='small' onClick={() => setParam('view', 'all')} sx={{ textTransform: 'none', fontWeight: 600, fontSize: 12.5, color: C.accent }}>
                Show all
              </Button>
            )}
          </Box>

          {merchants && rows.length === 0 ? (
            <Box sx={{ p: 2 }}>
              <EmptyState dense title='No merchants match'
                body={search ? 'Try another name or id, or clear the search.' : `No PTL merchant is in "${activeView.label}" right now.`} />
            </Box>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size='small' sx={{ minWidth: 900 }} aria-label='PTL merchants and their onboarding progress'>
                <TableHead>
                  <TableRow>
                    {['Merchant', 'Onboarding progress', 'Can book', 'Agreement', ''].map((h) => (
                      <TableCell key={h} sx={{ ...TYPE.eyebrow, fontSize: 10, color: C.inkFaint, bgcolor: C.surfaceAlt, borderColor: C.line, py: 1.25, whiteSpace: 'nowrap', ...(h === '' ? STICKY_END : {}) }}>
                        {h || <Box component='span' sx={SR_ONLY}>Action</Box>}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!merchants && [0, 1, 2, 3, 4].map((i) => (
                    <TableRow key={i}>{[0, 1, 2, 3, 4].map((j) => <TableCell key={j}><Skeleton variant='text' /></TableCell>)}</TableRow>
                  ))}
                  {rows.slice(page * size, page * size + size).map((m) => (
                    <MerchantRow key={m.id} m={m} onStep={(key) => navigate(ROUTES.onboardingStep(m.id, key))} onOpen={() => openNext(m)} onAgreement={() => setAgreementFor(m)} />
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
          {rows.length > 0 && (
            <TablePagination component='div' count={rows.length} page={page} onPageChange={(_, p) => setPage(p)} rowsPerPage={size}
              onRowsPerPageChange={(e) => { setSize(Number(e.target.value)); setPage(0) }} rowsPerPageOptions={PAGE_SIZES}
              sx={{ borderTop: `1px solid ${C.line}` }} />
          )}
        </Card>
      )}

      <OnboardMerchantDialog open={pickerOpen} onClose={() => setPickerOpen(false)}
        isDone={(id) => doneIds.has(id)}
        onOpenUpdate={(id) => { setPickerOpen(false); navigate(ROUTES.updateHub(id)) }}
        onDone={(id) => {
          setPickerOpen(false)
          // A merchant made PTL a moment ago is not in the loaded list yet: its first step is Pricing.
          const m = (merchants || []).find((x) => x.id === id)
          if (m) openNext(m)
          else navigate(ROUTES.onboardingStep(id, 'pricing'))
        }} />
      <AgreementDialog merchant={agreementFor} open={!!agreementFor} onClose={() => setAgreementFor(null)} onChanged={load} />
    </PtlPage>
  )
}

/** Short names of what booking still needs, for one table line. */
const NEED_SHORT = { 'KYC approval': 'KYC', 'a pickup location': 'Pickup', 'a rate card': 'Rate card' }

/** "Ready", or "Not yet" with what is missing on one line (the full KYC state is in the tooltip). */
function BookingReadiness({ m }) {
  const r = readiness(m)
  if (r.ready) return <ReadinessChip tone='done' label='Ready' />
  return (
    <Tooltip arrow title={`${r.kyc.label} · ${r.pickup.label}${statusOf(m).pricing ? '' : ' · no rate card'}`}>
      <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
        <ReadinessChip tone='blocked' label='Not yet' />
        <Typography component='span' sx={{ ...TYPE.meta, color: C.inkMuted, maxWidth: 170 }}>
          Needs {r.missing.map((x) => NEED_SHORT[x] || x).join(' · ')}
        </Typography>
      </Box>
    </Tooltip>
  )
}

/** The agreement chip; opens the agreement. Does not affect "Can book". */
function AgreementCell({ m, onOpen }) {
  const s = agreementStatus(m.agreement)
  return (
    <Box component='button' type='button' onClick={onOpen} aria-label={`Agreement of ${m.name}: ${s.label}. Open`}
      sx={{ all: 'unset', cursor: 'pointer', display: 'inline-flex', minHeight: 32, alignItems: 'center', borderRadius: RADIUS.pill, whiteSpace: 'nowrap', '&:focus-visible': { outline: `2px solid ${C.accent}`, outlineOffset: 2 } }}>
      <ReadinessChip tone={s.tone} label={s.label} />
    </Box>
  )
}

/** One merchant: its three steps as done chips / step buttons, n/3, and the main action. */
function MerchantRow({ m, onStep, onOpen, onAgreement }) {
  const states = stepStates(m)
  const next = nextStepKey(m, ONBOARDING_STEPS)
  const doneCount = ONBOARDING_STEPS.filter((s) => states[s.key].done).length
  const complete = !next
  const cards = m.rateCardCouriers || []
  return (
    <TableRow hover sx={{ '& td': { borderColor: C.lineSoft, py: 1.25, verticalAlign: 'middle' }, '&:hover td.sticky-end': { bgcolor: C.surfaceSunken } }}>
      <TableCell sx={{ minWidth: 180 }}>
        <Typography sx={TYPE.rowLabel}>{m.name}</Typography>
        {m.companyName && m.companyName !== m.name && <Typography sx={{ ...TYPE.meta, color: C.inkFaint }}>{m.companyName}</Typography>}
      </TableCell>
      <TableCell>
        {/* One line, so the three steps always read left to right; the table scrolls sideways when narrow. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, whiteSpace: 'nowrap' }}>
          {ONBOARDING_STEPS.map((s, i) => {
            const st = states[s.key]
            const label = s.key === 'tat' ? `TAT · ${st.custom ? 'Custom' : 'Standard'}`
              : s.key === 'allocation' && st.done ? `Allocation · ${st.custom ? 'Picked' : 'Cheapest'}` : s.label
            if (st.done) {
              return (
                <Box key={s.key} component='button' type='button' onClick={() => onStep(s.key)} title={`Step ${i + 1} done${s.key === 'pricing' && cards.length ? `: rate cards for ${cards.join(', ')}` : ''}. Open to review.`}
                  sx={{
                    all: 'unset', boxSizing: 'border-box', position: 'relative', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 0.5,
                    minHeight: 28, px: 1, borderRadius: RADIUS.pill, fontSize: 11.5, fontWeight: 700,
                    color: C.ok, bgcolor: C.okSoft, border: `1px solid ${C.okBorder}`,
                    '&:hover': { borderColor: C.ok }, '&:focus-visible': { outline: `2px solid ${C.accent}`, outlineOffset: 2 },
                  }}>
                  <CheckCircleIcon sx={{ fontSize: 14 }} aria-hidden />{label}
                  <Box component='span' sx={SR_ONLY}> (done)</Box>
                </Box>
              )
            }
            const isNext = next === s.key
            const locked = !!st.lockedBy
            const btn = (
              <Button size='small' disableElevation variant={isNext ? 'contained' : 'outlined'} disabled={locked}
                onClick={() => onStep(s.key)}
                sx={{
                  textTransform: 'none', fontWeight: 600, fontSize: 11.5, minHeight: 28, py: 0.25, px: 1, minWidth: 0, borderRadius: RADIUS.pill,
                  ...(isNext ? { bgcolor: C.accent, '&:hover': { bgcolor: C.accentHover } } : { borderStyle: 'dashed', color: C.inkMuted, borderColor: C.lineStrong }),
                }}>
                {isNext ? `Next · ${label}` : label}
              </Button>
            )
            return (
              <Tooltip key={s.key} arrow title={locked ? st.lockedBy : `Step ${i + 1}: set up ${s.label.toLowerCase()}`}>
                <span>{btn}</span>
              </Tooltip>
            )
          })}
          <Box component='span' sx={{ ...SX.pill, ml: 0.25, ...(complete ? { color: C.ok, bgcolor: C.okSoft } : { color: C.inkMuted, bgcolor: C.surfaceAlt }) }}>
            {complete ? 'Complete' : `${doneCount}/${ONBOARDING_STEPS.length}`}
          </Box>
        </Box>
      </TableCell>
      <TableCell>
        <BookingReadiness m={m} />
      </TableCell>
      <TableCell>
        <AgreementCell m={m} onOpen={onAgreement} />
      </TableCell>
      <TableCell align='right' className='sticky-end' sx={{ ...STICKY_END, bgcolor: C.surface }}>
        <Button size='small' variant={complete ? 'outlined' : 'contained'} endIcon={<ChevronRightIcon />} onClick={onOpen}
          sx={complete ? { ...SX.ctaGhost, fontSize: 12.5, minHeight: 32 } : { ...SX.cta, fontSize: 12.5, minHeight: 32 }}>
          {complete ? 'Update' : 'Continue'}
        </Button>
      </TableCell>
    </TableRow>
  )
}
